import type {MenuItemConfig} from '@myparcel-frontend/ui';
import {useProposition} from '@myparcel-frontend/proposition';
import {getDashboardMenu} from './modules/dashboard';
import {getDeliveriesMenu} from './modules/deliveries';
import {getDividerMenuItem} from './modules/divider';
import {getFinancialMenu} from './modules/finance';
import {getFulfilmentMenu} from './modules/fulfilment';
import {getOrdersMenu} from './modules/orders';
import {getProductsMenu} from './modules/products';
import {getShipmentsMenu} from './modules/shipment';
import {getShopSettingsMenu} from './modules/shop-settings';
import {getSubscriptionsMenu} from './modules/subscriptions';
import {getSupportMenu} from './modules/support';
import {getWebshopMenu} from './modules/webshop';
import useAccount from '@/composables/useAccount/useAccount';
import useAcl from '@/composables/useAcl';
import useFeatures from '@/composables/useFeatures';
import {useOrderVersion} from '@/composables/useOrderVersion/useOrderVersion';
import {useServiceAccount} from '@/composables/useServiceAccount/useServiceAccount';
import useShop from '@/composables/useShop/useShop';

export const getMenuConfig = (): MenuItemConfig[] => {
  const {accountSettings} = useAccount();
  const {currentShop} = useShop();
  const {hasPermission} = useAcl();
  const {isFeatureActive} = useFeatures();
  const {proposition} = useProposition();

  const currentShopId = currentShop.value.id;

  const {hasOrderManagement, hasLegacyOrderManagement} = useOrderVersion();

  const menu = [
    getDashboardMenu(),
    getOrdersMenu({
      hasOrderManagement,
      hasLegacyOrderManagement,
      hasOrdersCsvImport: isFeatureActive('page.orders.csvImport'),
    }),
    getShipmentsMenu({
      collectTrip: proposition.value.collectTrip,
      enableLocationFinder: proposition.value.enableLocationFinder,
      hasShipmentsOverview: proposition.value.shipments.hasShipmentsOverview,
    }),
  ];

  const {isFulfilmentActive, isSalesChannelsAccessible, isOrderRulesAccessible} = useServiceAccount();

  if (isFulfilmentActive.value) {
    menu.push(getProductsMenu());
  }

  if (isFulfilmentActive.value) {
    menu.push(getDeliveriesMenu());
  }

  if (proposition.value.applications.webshop.enabled) {
    menu.push(
      getWebshopMenu({
        webshopUrl: proposition.value.applications.webshop.url,
        allowNewPackaging: hasPermission('accountSelectOrder'),
      }),
    );
  }

  menu.push(
    getFinancialMenu({
      enableRatesOverview: proposition.value.enableRatesOverview,
    }),
    getSupportMenu({
      enableAcademy: proposition.value.enableAcademy,
      enableFAQ: proposition.value.applications.faq.enabled,
      enableTellAFriend: proposition.value.enableTellAFriend,
      FAQUrl: proposition.value.applications.faq.enabled ? proposition.value.applications.faq.url : '',
    }),
    getDividerMenuItem({
      label: 'account-divider',
    }),
    getSubscriptionsMenu(),
  );

  if (proposition.value.enableFulfilment) {
    menu.push(
      getFulfilmentMenu({
        isFulfilmentActive: Boolean(accountSettings.value.fulfilment),
        showRequestItem: Boolean(accountSettings.value.show_fulfilment_menu_item),
      }),
    );
  }

  menu.push(
    getShopSettingsMenu({
      shopId: String(currentShopId),
      enableGeneralSettings: isFeatureActive('page.settings.shop.general'),
      enableSalesChannels: proposition.value.enableSalesChannels,
      enableSalesChannelsFeature: isSalesChannelsAccessible.value,
      enableFulfilment: proposition.value.enableFulfilment,
      isFulfilmentActive: Boolean(accountSettings.value.fulfilment),
      enableEmailCenter: proposition.value.enableEmailCenter,
      enableIntegrations: proposition.value.settings.enableIntegrations,
      isOrderRulesAccessible: isOrderRulesAccessible.value,
      enableReturn: proposition.value.settings.shop.enableReturns,
    }),
  );

  return menu;
};
